import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Circle, MessageSquare } from 'lucide-react';
import api from '@/lib/api';
import { useWebSocket } from '@/contexts/WebSocketContext';
import { MemberAvatar } from '@/components/MemberAvatar';
import logger from '@/lib/logger';

/**
 * Live presence panel — shows organization teammates with green/gray
 * online indicators. Subscribes to ``presence_changed`` WS events so the
 * status flips in real time without polling.
 */
export function OnlineMembersPanel() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addListener } = useWebSocket();
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.getOnlineMembers()
      .then(r => setMembers(r.data || []))
      .catch(err => logger.error(err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!addListener) return;
    const unsub = addListener('presence_changed', (_event, data) => {
      setMembers(prev => {
        const idx = prev.findIndex(m => m.id === data.user_id);
        if (idx === -1) return prev;
        const next = [...prev];
        next[idx] = { ...next[idx], online: data.status === 'online' };
        // Re-sort: online first, then alphabetical
        next.sort((a, b) => (a.online === b.online ? (a.name || '').localeCompare(b.name || '') : (b.online ? 1 : -1)));
        return next;
      });
    });
    return unsub;
  }, [addListener]);

  const onlineCount = members.filter(m => m.online).length;

  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-sm bg-white dark:bg-slate-900 overflow-hidden" data-testid="online-members-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-800/40">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-[#0047AB]" />
          <h3 className="text-xs font-semibold text-slate-900 dark:text-slate-100 uppercase tracking-[0.15em]">Team Online</h3>
        </div>
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-400" data-testid="online-count">
          <span className="relative flex w-2 h-2">
            <span className={`absolute inset-0 ${onlineCount > 0 ? 'bg-emerald-400 animate-ping' : 'bg-slate-300'} rounded-full opacity-75`} />
            <span className={`relative w-2 h-2 ${onlineCount > 0 ? 'bg-emerald-500' : 'bg-slate-400'} rounded-full`} />
          </span>
          {onlineCount} online
        </span>
      </div>
      <div className="max-h-72 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin w-5 h-5 border-2 border-slate-900 border-t-transparent rounded-full" />
          </div>
        ) : members.length === 0 ? (
          <p className="text-center text-xs text-slate-400 py-10 px-4">No teammates yet. Invite members from the Members page.</p>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {members.slice(0, 10).map(m => (
              <li
                key={m.id}
                className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer group"
                onClick={() => navigate(`/messages?dm=${m.id}`)}
                data-testid={`online-member-${m.id}`}
              >
                <div className="relative shrink-0">
                  <MemberAvatar userId={m.id} name={m.name} size="sm" />
                  <Circle
                    className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 fill-current stroke-white dark:stroke-slate-900 stroke-2 rounded-full ${
                      m.online ? 'text-emerald-500' : 'text-slate-300'
                    }`}
                    data-testid={`presence-dot-${m.id}`}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{m.name || m.email}</p>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">{m.online ? 'Active now' : (m.role || 'member')}</p>
                </div>
                <MessageSquare className="w-3.5 h-3.5 text-slate-300 group-hover:text-[#0047AB] transition-colors" />
              </li>
            ))}
          </ul>
        )}
      </div>
      {members.length > 10 && (
        <button
          onClick={() => navigate('/members')}
          className="block w-full text-center text-[11px] text-[#0047AB] font-medium py-2 border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40"
          data-testid="view-all-members"
        >
          View all {members.length} members →
        </button>
      )}
    </div>
  );
}
